import { type NavigationAppearance, NavigationPosition } from '@unifyapps/defs/types/interface';
import { NavigationItemLabelVariantEnum, NavigationItemSelectionVariantEnum } from './types';

export const EXPANDED_SIDE_NAV_WIDTH = '!w-[280px]';
export const EXPANDED_SIDE_NAV_OVERLAY_WIDTH = 'w-[calc(100vw-280px)]';
export const EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT = 'start-[280px]';
export const EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT_VALUE = 280;
export const NON_EXPANDED_SIDE_NAV_REMAINING_SPACE_LEFT = 'start-[56px]';

export const DEFAULT_NAVIGATION_ITEM_APPEARANCE = {
  selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
  labelVariant: NavigationItemLabelVariantEnum.Expandable,
} as const;

export const UA_SIDE_NAV_ITEM = 'ua-side-nav-item';
export const UA_BOTTOM_NAV_ITEM = 'ua-bottom-nav-item';
export const UA_TOP_NAV_ITEM = 'ua-top-nav-item';
export const UA_END_NAV_ITEM = 'ua-end-nav-item';

export const NavigationItemClassName = {
  [NavigationPosition.Top]: UA_TOP_NAV_ITEM,
  [NavigationPosition.Bottom]: UA_BOTTOM_NAV_ITEM,
  [NavigationPosition.Start]: UA_SIDE_NAV_ITEM,
  [NavigationPosition.End]: UA_END_NAV_ITEM,
};

// stable DOM markers the test-studio element picker locates in deployed matrix apps.
// data-nav-root always sits on the same node as ua-side-nav in every variant.
export const DATA_NAV_ROOT_ATTRIBUTE = 'data-nav-root';
export const DATA_NAV_ITEM_ATTRIBUTE = 'data-nav-item';

export const UA_SIDE_NAV_APP_INFO_ICON = 'ua-app-side-nav-icon';

export const NAVBAR_DEFAULT_WIDTH = 56;
export const NAVBAR_START_STYLES_DEFAULT_WIDTH = `${NAVBAR_DEFAULT_WIDTH}px`;
export const NAVBAR_TOP_STYLES_DEFAULT_HEIGHT = '74px';
export const NAVBAR_BOTTOM_STYLES_DEFAULT_HEIGHT = '56px';
export const NAVBAR_BOTTOM_MOBILE_STYLES_DEFAULT_HEIGHT = '70px';

export const NAVBAR_BOTTOM_LABEL_UPFRONT_STYLES_DEFAULT_HEIGHT = '70px';
export const NAVBAR_BOTTOM_LABEL_NON_UPFRONT_STYLES_DEFAULT_HEIGHT = '56px';

export const NAVBAR_NON_BOTTOM_LABEL_UPFRONT_STYLES_DEFAULT_WIDTH = '70px';
export const NAVBAR_NON_BOTTOM_LABEL_NON_UPFRONT_STYLES_DEFAULT_WIDTH = '56px';

export const NAVBAR_EXPANDABLE_LABEL_DEFAULT_WIDTH = '56px';

export const NAVBAR_ROW_DEFAULT_WIDTH = '200px';
export const NAVBAR_COLUMN_DEFAULT_WIDTH = '70px';

export const NAVBAR_INITIAL_START_STYLES = {
  width: { custom: NAVBAR_START_STYLES_DEFAULT_WIDTH },
  height: 'h-auto',
  borderWidth: { r: 'border-e' },
  borderColor: 'border-secondary',
  borderRadius: {
    all: 'rounded-none',
  },
  gap: { y: 'gap-y-3xl' },
  padding: { t: 'pt-lg' },
  gapSecondary: { y: 'gap-y-lg' },
} as NavigationAppearance['styles'];

export const NAVBAR_INITIAL_TOP_STYLES = {
  width: 'w-auto',
  height: { custom: NAVBAR_TOP_STYLES_DEFAULT_HEIGHT },
  borderWidth: { b: 'border-b' },
  borderColor: 'border-secondary',
  borderRadius: {
    all: 'rounded-none',
  },
  padding: {
    custom: {
      r: 'pe-3xl',
      b: 'pb-lg',
      t: 'pt-lg',
      l: 'ps-3xl',
    },
  },
  gapSecondary: { x: 'gap-x-4xl' },
} as NavigationAppearance['styles'];

export const NAVBAR_INITIAL_BOTTOM_STYLES = {
  width: 'w-auto',
  height: { custom: NAVBAR_BOTTOM_STYLES_DEFAULT_HEIGHT },
  borderWidth: { t: 'border-t' },
  borderColor: 'border-secondary',
  borderRadius: {
    all: 'rounded-none',
  },
  padding: { all: 'p-0' },
} as NavigationAppearance['styles'];

// Initial styles for BottomNavigator in mobile applications
export const NAVBAR_INITIAL_BOTTOM_MOBILE_STYLES = {
  width: 'w-auto',
  height: { custom: NAVBAR_BOTTOM_MOBILE_STYLES_DEFAULT_HEIGHT },
  borderWidth: { t: 'border-t' },
  borderColor: 'border-secondary',
  borderRadius: {
    all: 'rounded-none',
  },
  padding: { all: 'p-0' },
};

// Initial styles for Upfront Row Navigation
export const NAVBAR_INITIAL_UPFRONT_ROW_STYLES = {
  padding: {
    custom: { t: 'pt-sm', r: 'pe-lg', b: 'pb-sm', l: 'ps-lg' },
  },
  gap: { x: 'gap-x-md' },
};

export const NAVBAR_INITIAL_UPFRONT_COLUMN_STYLES = {
  gap: { y: 'gap-y-md' },
};
